import * as React from 'react';
import { Box, Button, Checkbox, FormControlLabel, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import { FlexBoxInline, LinkSx, PageHeaderSx, PrimaryBtnSx, RemoveBtnSx, ThirdBtnSx } from '../../@shared/templates';
import { useDispatch, useSelector } from 'react-redux';
import { getIsAuthorized, getSeats, getSeatsIsLoading } from '../../reducers/main/mainReducer';
import { Palette, Sizes } from '../../@shared/palette';
import moment from 'moment';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { compose } from 'redux';
import { withManageSubscription } from '../../hoc/withManageSubscription';
import { setIsChangeSubscriptionDialogOpen, setIsManageSubscriptionDialogOpen, setIsSubscriptionDialogOpen } from '../../reducers/subscription/subscriptionReducer';
import { withChangeSubscription } from '../../hoc/withChangeSubscription';
import { SubscriptionProviderInstance } from '../../@shared/services/SubscriptionProvider';
import { NotificationModel, NotificationModelType } from '../../reducers/main/mainModel';
import { onThrowAlert } from '../../sagas/dataSaga/dataSagaActions';
import { AlertComponent } from '../../components/AlertComponent';


const Subscription = () => {
  const dispatch = useDispatch();
  const user = useSelector(getIsAuthorized);
  const seats = useSelector(getSeats)
  const seatsIsLoading = useSelector(getSeatsIsLoading)
  const [isCopied, setIsCopied] = React.useState<boolean>(false)
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false)
  // const appConfig = useSelector(getAppConfig)

  const [isCancellationConfirmOpen, setIsCancellationConfirmOpen] = React.useState<boolean>(false)
  const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false)
  const subscriptionProviderInstance = React.useMemo(() => SubscriptionProviderInstance, [])
  const getValue = (value: number) => {
    return value === 0 ? "Unlimited" : value.toString();
  }
  const isOwner = React.useMemo(() =>
    user?.license?.licensedUsers.find(x => x.externalUserId === user?.id)?.isOwner
    , [user?.id, user?.license?.licensedUsers])

  const onCopy = React.useCallback(async () => {
    if (user?.license?.id) {
      navigator.clipboard.writeText(user?.license?.id);
      setIsCopied(true);
    }
  }, [user?.license?.id]);

  const notification = React.useMemo(() => {
    return {
      type: NotificationModelType.Warning,
      message: `License has been expired ${user?.license ? moment().diff(moment(user.license.expirationDate), 'days') : ''} days ago`,
      date: new Date()
    }
  }, [user?.license]);

  return <Box sx={{ padding: "15px", width: "100%" }}>
    <ConfirmDialog isOpen={isCancellationConfirmOpen} onClose={() => setIsCancellationConfirmOpen(false)} title='Cancel subscription' onConfirmedAction={async () => {
      setIsProcessing(true);
      var res = await subscriptionProviderInstance.cancel();
      if (res.status === 200) {
        setIsCancellationConfirmOpen(false);
        dispatch(onThrowAlert({
          message: "Subscription successfully canceled",
          lifeTime: 3000,
          type: NotificationModelType.Warning
        } as NotificationModel));
      }
      else {
        dispatch(onThrowAlert({
          message: res.data,
          lifeTime: 5000,
          type: NotificationModelType.Error
        } as NotificationModel));
      }
      setIsProcessing(false);
    }} isProcessing={isProcessing} isConfirmDisabled={!isConfirmed} >
      <FormControlLabel sx={{ margin: 0 }} control={<Checkbox sx={{ padding: 0, marginRight: "10px" }} />} label={<Box>
        <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Confirm cancellation</Typography>
        <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary }}>The subscription will be canceled right after expiration</Typography>
      </Box>
      }
        onChange={(_, v) => { setIsConfirmed(v) }}
        value={isConfirmed} />
    </ConfirmDialog>
    <Typography sx={{ ...PageHeaderSx, marginBottom: "20px" }}>
      {user?.license ? "Subscription" : "No subscription"}
    </Typography>
    {user?.license && user.license.expirationDate && moment().utc().isAfter(moment(user.license.expirationDate)) &&
      <Box sx={{ marginBottom: "20px" }}>
        <AlertComponent notification={notification} isClosable={false} />
      </Box>
    }
    <Box sx={{ ...FlexBoxInline, marginBottom: "5px" }}>
      {user?.license?.id && <>
        <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, margin: 0 }}>Id:</Typography>
        <Tooltip title={isCopied ? "Copied!" : "Copy"} placement="top" arrow>
          <Button sx={{ ...ThirdBtnSx }} onClick={onCopy}>{user?.license?.id}</Button>
        </Tooltip>
      </>}
      {!user?.license?.id && <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>
        Id: None
      </Typography>}
      <Link sx={{ ...LinkSx }} component="button" variant="body2" onClick={() => { dispatch(setIsChangeSubscriptionDialogOpen(true)) }}>Change</Link>
    </Box>
    <Box sx={{ ...FlexBoxInline, marginBottom: "5px" }}>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>
        Type: {user?.license?.licenseType || "None"}
      </Typography>
    </Box>
    {user?.license && <>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Creation date: {moment(user?.license?.createdDate).format("DD/MM/YYYY hh:mm")}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Expiration date: {moment(user?.license?.expirationDate).format("DD/MM/YYYY hh:mm")}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Count of models per schema: {getValue(user?.license?.countOfModels)}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Count of schemas: {user.licenseUsage.countOfSchemas} of {getValue(user?.license?.countOfSchemas)}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        CPU minutes per day: {user.licenseUsage.cpuUsage} of {getValue(user?.license?.cpuTimeInMinutes)}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Requests per minute: {getValue(user?.license?.requestsPerMinute)}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Datasets MB per schema: {getValue(user?.license?.datasetSizeForSchemaMb)}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Model in queue: {user.licenseUsage.modelsInQueue} of {getValue(user?.license?.maxCountModelsInQueue)}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Max number of features per schema: {getValue(user?.license?.numberOfFeatures)}
      </Typography>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Calibration: {user?.license?.isCalibratingEnabled ? "Enabled" : "Disabled"}
      </Typography>
      {/* {appConfig.isSelfHostedServiceEnabled &&
        <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
          Number of instances: {user?.licenseUsage?.numberOfInstances} of {user?.license.numberOfInstances}
        </Typography>} */}
      {seatsIsLoading && <Skeleton variant="text" width={200} sx={{ fontSize: Sizes.textPrimary, marginBottom: "5px" }} />}
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px" }}>
        Seats: {user?.license?.isSupportsSeats ? "Enabled" : "Disabled"}
      </Typography>
      {!seatsIsLoading && user?.license?.isSupportsSeats && isOwner && <Box sx={{ ...FlexBoxInline, marginBottom: "5px" }}>
        <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>
          Number of seats: {seats.length} of {user?.license.numberOfSeats}
        </Typography>
        <Link sx={LinkSx} component="button" variant="body2" onClick={() => { dispatch(setIsManageSubscriptionDialogOpen(true)) }}>Manage</Link>
      </Box>}
    </>}

    <Box sx={{ ...FlexBoxInline, marginTop: "20px" }}>
      <Button sx={PrimaryBtnSx} onClick={() => dispatch(setIsSubscriptionDialogOpen(true))}>Upgrade subscription</Button>
      <Button disabled={!user?.license || user?.license?.isCancellationRequested} sx={RemoveBtnSx} onClick={() => setIsCancellationConfirmOpen(true)}>Cancel subscription</Button>
    </Box>
    <Box>
      <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginTop: "20px" }}>
        By using our service, you agree to our&nbsp;
        <Link sx={LinkSx} href="https://mlartisan.com/terms" target="_blank">Terms of Service</Link>
        &nbsp;and&nbsp;
        <Link sx={LinkSx} href="https://mlartisan.com/privacy" target="_blank">Privacy Policy</Link>
      </Typography>
    </Box>
  </Box>
}

export default compose<React.ComponentType>(
  withManageSubscription,
  withChangeSubscription
)(Subscription);
